import productCorpus from '../../../dashboard/api/productcorpus';

const makePage = (page = 0, size = 10, total = 0) => {
  const totalPages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1;
  return {
    currentPage: page,
    pageSize: size,
    totalElements: total,
    totalPages,
    hasPrevious: page > 0,
    hasNext: page + 1 < totalPages,
  };
};

const normPagination = (body, fallbackPage, { page, size }) => {
  if (body?.pagination) {
    const p = body.pagination;
    const current = Math.max(0, p.currentPage ?? p.page ?? 0);
    const sizeV = p.pageSize ?? size ?? 10;
    const total = p.totalElements ?? p.total ?? 0;
    return { ...fallbackPage, ...makePage(current, sizeV, total) };
  }
  if (typeof body?.totalProducts === 'number') {
    return makePage(
      page ?? 0,
      size ?? fallbackPage.pageSize,
      body.totalProducts
    );
  }
  return fallbackPage;
};
const idxById = (arr, id) => arr.findIndex(p => String(p.id) === String(id));
const clamp = (n, min = 0) => (n < min ? min : n);

const state = () => ({
  accountId: null,
  products: [],
  formProducts: [],
  fileProducts: [],
  files: [],

  allPagination: makePage(0, 10, 0),
  formPagination: makePage(0, 10, 0),

  search: '',
  sortBy: 'createdAt',
  sortDirection: 'DESC',

  loadingAll: false,
  loadingForm: false,
  loadingFiles: false,

  errorAll: null,
  errorForm: null,
  errorFiles: null,

  reqAll: 0,
  reqForm: 0,
  reqFiles: 0,

  uiFlags: { isInitialized: false, lastFetchedAt: 0 },

  lastFileUploadTime: null,
});

export const getters = {
  // data
  accountId: s => s.accountId,
  products: s => s.products,
  formProducts: s => s.formProducts,
  fileProducts: s => s.fileProducts,
  files: s => s.files,

  // pagination
  allPagination: s => s.allPagination,
  formPagination: s => s.formPagination,
  allProductsMeta: s => s.allPagination,
  totalProducts: s => {
    const formCount = (s.formProducts || []).length;
    const fileCount = (s.fileProducts || []).length;
    return formCount + fileCount;
  },

  // query
  search: s => s.search,
  sortBy: s => s.sortBy,
  sortDirection: s => s.sortDirection,

  // loading
  loadingAll: s => s.loadingAll,
  loadingForm: s => s.loadingForm,
  loadingFiles: s => s.loadingFiles,

  // errors
  errorAll: s => s.errorAll,
  errorForm: s => s.errorForm,
  errorFiles: s => s.errorFiles,

  // UI helpers
  isAllEmpty: s => !s.loadingAll && s.products.length === 0,
  isFormEmpty: s => !s.loadingForm && s.formProducts.length === 0,
  isFilesEmpty: s => !s.loadingFiles && s.files.length === 0,

  uiFlags: s => s.uiFlags,
  lastFileUploadTime: s => s.lastFileUploadTime,

  knownHasFormProducts: s =>
    (s.formProducts?.length ?? 0) > 0 ||
    (s.formPagination?.totalElements ?? 0) > 0,
};

export const mutations = {
  // data
  setAccountId(s, id) {
    s.accountId = id || null;
  },
  setProducts: (s, v) => {
    s.products = Array.isArray(v) ? v : [];
  },
  setFormProducts: (s, v) => {
    s.formProducts = Array.isArray(v) ? v : [];
  },
  setFileProducts: (s, v) => {
    s.fileProducts = Array.isArray(v) ? v : [];
  },
  setFiles: (s, v) => {
    s.files = Array.isArray(v) ? v : [];
  },

  // pagination
  setAllPagination: (s, p = {}) => {
    s.allPagination = { ...s.allPagination, ...p };
  },
  setFormPagination: (s, p = {}) => {
    s.formPagination = { ...s.formPagination, ...p };
  },

  // query
  setSearch: (s, q) => {
    s.search = q || '';
  },
  setSortBy: (s, v) => {
    s.sortBy = v || 'createdAt';
  },
  setSortDirection: (s, v) => {
    s.sortDirection = (v || 'DESC').toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
  },

  // loading/errors
  setLoadingAll: (s, v) => {
    s.loadingAll = !!v;
  },
  setLoadingForm: (s, v) => {
    s.loadingForm = !!v;
  },
  setLoadingFiles: (s, v) => {
    s.loadingFiles = !!v;
  },

  setErrorAll: (s, e) => {
    s.errorAll = e || null;
  },
  setErrorForm: (s, e) => {
    s.errorForm = e || null;
  },
  setErrorFiles: (s, e) => {
    s.errorFiles = e || null;
  },

  // request guards
  bumpReqAll: s => {
    s.reqAll += 1;
  },
  bumpReqForm: s => {
    s.reqForm += 1;
  },
  bumpReqFiles: s => {
    s.reqFiles += 1;
  },

  // misc
  setUiFlags: (s, flags) => {
    s.uiFlags = { ...s.uiFlags, ...(flags || {}) };
  },
  setLastFileUploadTime: (s, time) => {
    s.lastFileUploadTime = time;
  },

  // resetters
  resetAllState(s) {
    s.products = [];
    s.allPagination = makePage(0, s.allPagination.pageSize, 0);
    s.search = '';
    s.sortBy = 'createdAt';
    s.sortDirection = 'DESC';
    s.errorAll = null;
  },
  resetFormState(s) {
    s.formProducts = [];
    s.formPagination = makePage(0, s.formPagination.pageSize, 0);
    s.errorForm = null;
  },
  resetByAccount(s) {
    s.files = [];
    s.fileProducts = [];
    s.errorFiles = null;
  },
  // --- helpers ---

  // --- mutations ---
  upsertProducts(s, items = []) {
    if (!Array.isArray(items) || !items.length) return;
    const next = [...s.products];
    items.forEach(it => {
      const i = idxById(next, it.id);
      if (i >= 0) next[i] = { ...next[i], ...it };
      else next.unshift(it);
    });
    if (next.length > s.allPagination.pageSize) {
      next.length = s.allPagination.pageSize;
    }
    s.products = next;
  },

  removeProductsById(s, ids = []) {
    if (!Array.isArray(ids) || !ids.length) return;
    const idset = new Set(ids.map(String));
    s.products = s.products.filter(p => !idset.has(String(p.id)));
    const total = clamp(s.allPagination.totalElements - ids.length, 0);
    s.allPagination = {
      ...s.allPagination,
      ...makePage(s.allPagination.currentPage, s.allPagination.pageSize, total),
    };
  },

  bumpTotal(s, delta = 0) {
    const total = clamp((s.allPagination.totalElements || 0) + delta, 0);
    s.allPagination = {
      ...s.allPagination,
      ...makePage(s.allPagination.currentPage, s.allPagination.pageSize, total),
    };
  },

  upsertFormProducts(s, items = []) {
    if (!Array.isArray(items) || !items.length) return;
    const next = [...s.formProducts];
    items.forEach(it => {
      const i = idxById(next, it.id);
      if (i >= 0) next[i] = { ...next[i], ...it };
      else next.unshift(it);
    });
    if (next.length > s.formPagination.pageSize) {
      next.length = s.formPagination.pageSize;
    }
    s.formProducts = next;
  },
  // mutations
  removeFormProductsById(s, ids = []) {
    if (!Array.isArray(ids) || !ids.length) return;
    const idset = new Set(ids.map(String));
    s.formProducts = s.formProducts.filter(p => !idset.has(String(p.id)));
    const total = Math.max(
      0,
      (s.formPagination.totalElements || 0) - ids.length
    );
    s.formPagination = {
      ...s.formPagination,
      ...makePage(
        s.formPagination.currentPage,
        s.formPagination.pageSize,
        total
      ),
    };
  },
  bumpFormTotal(s, delta = 0) {
    const total = Math.max(0, (s.formPagination.totalElements || 0) + delta);
    s.formPagination = {
      ...s.formPagination,
      ...makePage(
        s.formPagination.currentPage,
        s.formPagination.pageSize,
        total
      ),
    };
  },
  purgeOptimisticProducts(s) {
    s.products = s.products.filter(p => !p.optimistic);
    s.formProducts = s.formProducts.filter(p => !p.optimistic);
    s.fileProducts = s.fileProducts.filter(p => !p.optimistic);
  },
  removeFromFilesByProductIds(s, ids = []) {
    if (!Array.isArray(ids) || !ids.length) return;
    const idset = new Set(ids.map(String));

    // files[].products و productsCount
    s.files = s.files.map(f => {
      const nextProducts = (f.products || []).filter(
        p => !idset.has(String(p.id))
      );
      const nextCount =
        typeof f.productCount === 'number'
          ? Math.max(
              0,
              f.productCount - ((f.products || []).length - nextProducts.length)
            )
          : nextProducts.length;
      return { ...f, products: nextProducts, productCount: nextCount };
    });

    // fileProducts (فهرست تخت)
    s.fileProducts = (s.fileProducts || []).filter(
      p => !idset.has(String(p.id))
    );
  },

  removeFileById(s, fileId) {
    if (!fileId) return;
    const id = String(fileId);

    s.files = s.files.filter(f => String(f.id) !== id);
    s.fileProducts = s.fileProducts.filter(p => String(p.fileId) !== id);
  },
};

const normErr = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const actions = {
  setUserId(_, userId) {
    productCorpus.setUserId?.(userId);
  },
  setAccountId({ commit, state: s }, accountId) {
    if (s.accountId === accountId) {
      return;
    }
    productCorpus.setAccountId?.(accountId);
    commit('setAccountId', accountId);
    commit('resetAllState');
    commit('resetFormState');
    commit('resetByAccount');
    commit('setUiFlags', { lastFetchedAt: 0 });
  },
  async fetchAll({ commit, state: s }, payload = {}) {
    commit('bumpReqAll');
    const reqId = s.reqAll;

    const page = payload.page ?? s.allPagination.currentPage ?? 0;
    const size = payload.size ?? s.allPagination.pageSize ?? 10;

    commit('setLoadingAll', true);
    commit('setErrorAll', null);
    try {
      const { data } = await productCorpus.listAll({
        page,
        size,
        sortBy: s.sortBy,
        sortDirection: s.sortDirection,
        search: s.search || undefined,
      });
      if (s.reqAll !== reqId) return;

      const body = data?.data || {};
      const products = Array.isArray(body?.products) ? body.products : [];
      const pagination = body?.pagination || {};
      commit('setProducts', products);
      commit(
        'setAllPagination',
        normPagination(body, s.allPagination, { page, size })
      );
      commit('setUiFlags', { lastFetchedAt: Date.now() });
    } catch (e) {
      if (s.reqAll !== reqId) return;
      commit('setErrorAll', normErr(e, 'Failed to fetch all products'));
      commit('setProducts', []);
      commit('setAllPagination', makePage(0, size, 0));
    } finally {
      if (s.reqAll === reqId) commit('setLoadingAll', false);
    }
  },

  async fetchForm({ commit, state: s }, payload = {}) {
    const { force = false } = payload;

    if (s.loadingForm && !force) return;

    commit('bumpReqForm');
    const reqId = s.reqForm;

    const page = payload.page ?? s.formPagination.currentPage ?? 0;
    const size = payload.size ?? s.formPagination.pageSize ?? 10;

    commit('setLoadingForm', true);
    commit('setErrorForm', null);
    try {
      const { data } = await productCorpus.listForm({
        page,
        size,
        sortBy: s.sortBy,
        sortDirection: s.sortDirection,
        search: s.search || undefined,
      });
      if (s.reqForm !== reqId) return;

      const body = data?.data ?? data ?? {};
      let products = [];
      if (Array.isArray(body?.products)) {
        products = body.products;
      } else if (Array.isArray(body?.data?.products)) {
        products = body.data.products;
      }

      commit('setFormProducts', products);
      commit(
        'setFormPagination',
        normPagination(body, s.formPagination, { page, size })
      );
      commit('setUiFlags', { isInitialized: true });
    } catch (e) {
      if (s.reqForm !== reqId) return;
      commit('setErrorForm', normErr(e, 'Failed to fetch form products'));
      commit('setFormProducts', []);
      commit('setFormPagination', makePage(0, size, 0));
    } finally {
      if (s.reqForm === reqId) commit('setLoadingForm', false);
    }
  },

  async fetchFiles({ commit, state: s }, { force = false } = {}) {
    if (s.loadingFiles && !force) return;

    commit('bumpReqFiles');
    const reqId = s.reqFiles;

    commit('setLoadingFiles', true);
    commit('setErrorFiles', null);
    try {
      const { data } = await productCorpus.listFile();
      if (s.reqFiles !== reqId) return;

      const files = Array.isArray(data?.data) ? data.data : [];
      commit('setFiles', files);
      const flat = files.flatMap(f =>
        Array.isArray(f.products)
          ? f.products.map(p => ({ ...p, fileId: f.id }))
          : []
      );
      commit('setFileProducts', flat);
    } catch (e) {
      if (s.reqFiles !== reqId) return;
      commit(
        'setErrorFiles',
        normErr(e, 'Failed to fetch files with products')
      );
      commit('setFiles', []);
      commit('setFileProducts', []);
    } finally {
      if (s.reqFiles === reqId) commit('setLoadingFiles', false);
    }
  },

  setSearch({ commit, dispatch }, q) {
    commit('setSearch', q);
    return dispatch('fetchAll', { page: 0 });
  },

  setSort({ commit, dispatch }, { sortBy, sortDirection }) {
    commit('setSortBy', sortBy);
    commit('setSortDirection', sortDirection);
    return dispatch('fetchAll', { page: 0 });
  },

  async uploadFiles({ dispatch, commit, rootGetters }, payload) {
    const files = Array.isArray(payload) ? payload : payload?.files;
    const onProgress =
      (!Array.isArray(payload) && payload?.onProgress) || undefined;

    const botId = rootGetters['newBotify/getBotifyId'];
    const isBotCreated = rootGetters['newBotify/getIsCreated'];

    if (!botId || !isBotCreated) {
      await dispatch('newBotify/create', {}, { root: true });
    }

    commit('setLoadingFiles', true);
    commit('setErrorFiles', null);
    try {
      await productCorpus.uploadFiles(files, { onUploadProgress: onProgress });

      // Set upload time to trigger refresh in components
      commit('setLastFileUploadTime', Date.now());

      await Promise.all([
        dispatch('fetchAll', { page: 0 }),
        dispatch('fetchFiles'),
        dispatch('fetchForm', { page: 0 }),
      ]);
      return { ok: true };
    } catch (e) {
      commit('setErrorFiles', normErr(e, 'File upload failed'));
      throw e;
    } finally {
      commit('setLoadingFiles', false);
    }
  },

  deleteFileLocally({ commit }, fileId) {
    commit('removeFileById', fileId);
  },

  async deleteFile({ dispatch, commit, state: s }, fileId) {
    commit('setLoadingFiles', true);
    commit('setErrorFiles', null);
    try {
      await productCorpus.deleteFile(fileId);
      await Promise.all([
        dispatch('fetchAll', { page: s.allPagination.currentPage }),
        dispatch('fetchFiles'),
      ]);
      if (!s.products.length && s.allPagination.hasPrevious) {
        await dispatch('fetchAll', {
          page: Math.max(0, s.allPagination.currentPage - 1),
        });
      }
    } catch (e) {
      commit('setErrorFiles', normErr(e, 'Delete failed'));
    } finally {
      commit('setLoadingFiles', false);
    }
  },

  async addProducts(
    { commit, dispatch, rootGetters, state: s },
    products
  ) {
    commit('setLoadingAll', true);
    commit('setErrorAll', null);

    const botId = rootGetters['newBotify/getBotifyId'];
    const isBotCreated = rootGetters['newBotify/getIsCreated'];

    if (!botId || !isBotCreated) {
      await dispatch('newBotify/create', {}, { root: true });
    }

    const optimistic = (products || [])
      .filter(p => p.id && p.id.toString().trim() !== '')
      .map(p => ({ ...p, optimistic: true }));
    if (optimistic.length) {
      commit('upsertFormProducts', optimistic);
      commit('bumpFormTotal', optimistic.length);
    }

    try {
      const { data } = await productCorpus.addProducts(products);
      const created = data?.data?.products ?? data?.products ?? [];
      if (created.length) {
        commit('purgeOptimisticProducts');
        commit(
          'upsertProducts',
          created.map(p => ({ ...p, optimistic: false }))
        );
        commit('bumpTotal', created.length - optimistic.length);
        commit(
          'upsertFormProducts',
          created.map(p => ({ ...p, optimistic: false }))
        );
        commit('bumpFormTotal', created.length - optimistic.length);
      }
    } catch (e) {
      commit('purgeOptimisticProducts');
      commit('setErrorAll', e?.message || 'Failed to add products');
      await dispatch('fetchAll', {
        page: s.allPagination.currentPage ?? 0,
      });
    } finally {
      commit('setLoadingAll', false);
    }
  },

  async updateProducts({ commit, dispatch, state: s }, products) {
    commit('setLoadingAll', true);
    commit('setErrorAll', null);

    try {
      const { data } = await productCorpus.updateProducts(products);
      const updated = data?.data?.products ?? data?.products ?? [];
      if (updated.length) {
        commit('purgeOptimisticProducts');
        commit(
          'upsertProducts',
          updated.map(p => ({ ...p, optimistic: false }))
        );
        commit(
          'upsertFormProducts',
          updated.map(p => ({ ...p, optimistic: false }))
        );
      }
    } catch (e) {
      commit('setErrorAll', e?.message || 'Failed to update products');
      await Promise.allSettled([
        dispatch('fetchAll', { page: s.allPagination.currentPage ?? 0 }),
        dispatch('fetchForm', { page: s.formPagination.currentPage ?? 0 }),
      ]);
    } finally {
      commit('setLoadingAll', false);
    }
  },

  async deleteProducts({ commit, state: s }, productIds) {
    commit('setLoadingAll', true);
    commit('setErrorAll', null);

    const prev = [...s.products];
    const prevForm = [...s.formProducts];
    const prevFiles = [...s.files];
    const prevFileProducts = [...s.fileProducts];
    const prevAllPagination = { ...s.allPagination };

    commit('removeProductsById', productIds);
    commit('removeFormProductsById', productIds);
    commit('removeFromFilesByProductIds', productIds);

    try {
      await productCorpus.deleteProducts(productIds);
    } catch (e) {
      commit('setErrorAll', e?.message || 'Failed to delete products');
      commit('setProducts', prev);
      commit('setFormProducts', prevForm);
      commit('setFiles', prevFiles);
      commit('setFileProducts', prevFileProducts);
      commit('setAllPagination', prevAllPagination);
    } finally {
      commit('setLoadingAll', false);
    }
  },
};

export default { namespaced: true, state, getters, mutations, actions };
